import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { useTranslate } from 'hooks/useTranslate';
import { AI_EVENTS } from 'modules/ai/constants/aiAnalyticsEvents';
import { submitAiFeedback } from 'modules/ai/api/submitAiFeedback';
import { setAiMessageFeedback } from 'modules/ai/redux/aiActions';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';

type AiMessageFeedbackProps = {
  messageId: string;
  conversationId: string | null;
  societyId: string;
  userQuestion?: string;
  response: string;
  initialFeedback?: 'positive' | 'negative' | null;
  disabled?: boolean;
  entryPoint?: 'embed' | 'agent_page';
};

const AiMessageFeedback: FC<AiMessageFeedbackProps> = ({
  messageId,
  conversationId,
  societyId,
  userQuestion = '',
  response,
  initialFeedback = null,
  disabled = false,
  entryPoint = 'agent_page',
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(
    initialFeedback
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeedback = async (value: 'positive' | 'negative') => {
    if (disabled || isSubmitting || feedback || !conversationId) return;

    setIsSubmitting(true);
    try {
      await submitAiFeedback({
        societyId,
        conversationId,
        messageId,
        feedback: value,
      });
      setFeedback(value);
      dispatch(setAiMessageFeedback({ messageId, feedback: value }));
      trackAiActivity({
        mixpanel,
        state: store.getState() as State,
        eventName: AI_EVENTS.FEEDBACK_SUBMITTED,
        extras: {
          conversationId,
          messageId,
          feedback: value,
          entryPoint,
          question: userQuestion,
          response,
        },
      });
    } catch {
      // silent — user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  if (feedback) {
    return (
      <p className="navi-assistant-message__feedback-thanks" role="status">
        {t('AIFeedbackThanks')}
      </p>
    );
  }

  return (
    <div
      className="navi-assistant-message__feedback"
      aria-label={t('AIFeedbackTooltip')}
    >
      <span className="navi-assistant-message__feedback-label">
        {t('AIFeedbackTooltip')}
      </span>
      <button
        type="button"
        className="navi-assistant-message__feedback-btn"
        disabled={disabled || isSubmitting}
        aria-label={t('AIFeedbackHelpful')}
        data-testid={`ai-feedback-positive-${messageId}`}
        onClick={() => handleFeedback('positive')}
      >
        <em className="icon ni ni-thumbs-up" aria-hidden />
      </button>
      <button
        type="button"
        className="navi-assistant-message__feedback-btn"
        disabled={disabled || isSubmitting}
        aria-label={t('AIFeedbackNotHelpful')}
        data-testid={`ai-feedback-negative-${messageId}`}
        onClick={() => handleFeedback('negative')}
      >
        <em className="icon ni ni-thumbs-down" aria-hidden />
      </button>
    </div>
  );
};

export default AiMessageFeedback;
